/**
 * @file: 选择器工具函数
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../cubeTree/index';
import * as Toolkit from '../../toolkit/index';
import Utils from '../../utils/util';
import Config from '../../config/index';
import CodiSystem from '../../codiSystem/index';
import SelectStore from './store/index';
import SelectStyle from './selectStyle';

// 可以被选中的cube
function canSelect(cube: CubeNode): boolean {
    let selectType: number = Config.get({
        moduleName: 'select',
        key: 'selectType'
    });

    // 任意选择
    if (selectType === 0) {
        return CubeTree.isShelf(cube.id)
            || CubeTree.isFreezer(cube.id)
            || CubeTree.isShelfPartition(cube.id)
            || CubeTree.isGoods(cube.id)
            || CubeTree.isTextarea(cube.id)
            || CubeTree.isBafflePlate(cube.id);
    }

    // 商品
    else if (selectType === 1) {
        return CubeTree.isGoods(cube.id);
    }

    // 组件
    else {
        return CubeTree.isShelfPartition(cube.id);
    }
}

function idCanSelect(target: CubeId): boolean {
    let targetCube: CubeNode = CubeTree.findByCubeId(target);

    return canSelect(targetCube);
}

// 清空选择器
function clearAllSelect(): void {
    SelectStyle.removeAllSelectStyle();
    SelectStore.clear();
}

/**
 * 新增选中cube，并对所有选中的cube加选中样式
 */
function addOne(cubeId: CubeId): void {
    // 从坐标系统中获取当前点击的cube的逻辑坐标
    let cubeCoordinate: LogicCoordinate = CodiSystem.getLogicCoordinate(cubeId);

    // 把当前点击的cube的逻辑坐标添加到选择器中，计算并存储最新的选择框边界
    SelectStore.addWrapRect({
        left: cubeCoordinate.left,
        top: cubeCoordinate.top,
        width: cubeCoordinate.width,
        height: cubeCoordinate.height
    });

    SelectStore.add([cubeId]);
    SelectStyle.cubeAddSelectStyle([cubeId]);
}

function removeOne(cubeId: string): void {
    SelectStore.remove([cubeId]);
    SelectStyle.cubeRemoveSelectStyle([cubeId]);
    updateWrapRectAsync();
}

function addGroup(cubeIds: Array<CubeId>): void {
    SelectStore.add(cubeIds);
    SelectStyle.cubeAddSelectStyle(cubeIds);
    updateWrapRectAsync();
}

function removeGroup(cubeIds: Array<CubeId>): void {
    SelectStore.remove(cubeIds);
    SelectStyle.cubeRemoveSelectStyle(cubeIds);
    updateWrapRectAsync();
}

function selectBatches(cubeIds: Array<string>): void {
    SelectStore.add(cubeIds);
    SelectStyle.addAllSelectStyle();
    updateWrapRectAsync();
}

function filterByTargetTypeCubeIds(types: Array<CubeType>, cubeIds: Array<string>): Array<string> {
    let typeList: Set<CubeType> = new Set(types);
    let hasPartition: boolean = CubeTree.hasPartition(Array.from(typeList));

    return cubeIds.filter((cubeId: string): boolean => {
        if (CubeTree.isPartition(cubeId)) {
            return hasPartition;
        }
        let cube: CubeNode = CubeTree.findByCubeId(cubeId);
        let cubeType: CubeType = CubeTree.getCubeType(cube);

        return typeList.has(cubeType);
    });
}

function updateWrapRectSync(): void {
    let allSelectedCubeIds: Array<CubeId> = SelectStore.getAll();
    let logicCoordinates: Array<LogicCoordinate> = CodiSystem.getLogicCoordinates(allSelectedCubeIds);
    let wrapRect: Rect = Toolkit.createRectsBorder(logicCoordinates);

    SelectStore.updateWrapRect({
        ...wrapRect
    });
}

// cube列表是否全部被选中
function cubeListInSelect(cubeIds: CubeId[]): boolean {
    return cubeIds.every((cubeId: CubeId): boolean => {
        return SelectStore.has(cubeId);
    });
}

const updateWrapRectAsync: (...args: any[]) => void = Utils.debounce(updateWrapRectSync, 50);

export {
    selectBatches,
    clearAllSelect,
    canSelect,
    addOne,
    removeOne,
    addGroup,
    filterByTargetTypeCubeIds,
    updateWrapRectSync,
    updateWrapRectAsync,
    removeGroup,
    cubeListInSelect,
    idCanSelect
};
